import { Controller } from "@hotwired/stimulus";

/**
 * Picking Photo Canvas Controller (Restaurant Audit,
 * Step 3).
 *
 * Drives the streaming-reveal of the "Tu mejor foto"
 * step.
 *
 * Architecture (CAL-264). The picking_photo canvas is
 * rendered ONCE on page load and never swapped by
 * `audit-stage`. All state is value-driven from
 * Stimulus values that the server updates via
 * `set_dataset_attributes` Turbo Stream actions. Pre-
 * rendered winner-card / score-row / discarded-tile
 * HTML chunks ship as JSON-encoded arrays of strings
 * on the dataset; we paste them into their slots.
 *
 * Lifecycle:
 *
 * 1. **connect()**: render whatever values are
 *    present at connect-time. If the payload was
 *    already on screen at page load (replay /
 *    already-done audit), the chunks are non-empty:
 *    we paste them in immediately. If empty, the
 *    server-rendered skeletons stay put until
 *    narration arrives.
 *
 * 2. **`narratedValueChanged`**: fires when the
 *    payload arrives via dataset broadcast. We render
 *    the narration pill, winner card, score rows and
 *    discarded tiles, then dispatch
 *    `audit-step:complete` so the Continue button
 *    enables.
 *
 * No time-based cascades. Animations from sub-
 * controllers (`audit-reveal`, `audit-count-up`,
 * `audit-bar-fill`, `audit-image-load`) inside the
 * pasted HTML chunks run naturally on whatever HTML
 * lands.
 */
export default class extends Controller {
  static values = {
    narrated: { type: String, default: "false" },
    reviewerFirstName: { type: String, default: "" },
    winnerCardHtml: { type: String, default: "" },
    scoreRowHtml: { type: Array, default: [] },
    discardedTileHtml: { type: Array, default: [] },
  };

  static targets = [
    "narrationPill",
    "winnerSlot",
    "scoreCardSlot",
    "discardedSlot",
  ];

  connect() {
    this.rendered = false;
    this.maybeRender();
  }

  narratedValueChanged(value, previousValue) {
    // Stimulus fires every value-changed callback
    // once on connect with previousValue=undefined.
    // The connect() flow handles initial state.
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.maybeRender();
  }

  winnerCardHtmlValueChanged(_v, prev) {
    if (prev === undefined) return;
    this.maybeRender();
  }

  scoreRowHtmlValueChanged(_v, prev) {
    if (prev === undefined) return;
    this.maybeRender();
  }

  discardedTileHtmlValueChanged(_v, prev) {
    if (prev === undefined) return;
    this.maybeRender();
  }

  // -------- render --------

  maybeRender() {
    if (this.rendered) return;
    if (this.narratedValue !== "true") return;

    this.renderNarrationPill();
    this.renderWinnerCard();
    this.renderScoreCard();
    this.renderDiscardedGrid();

    this.rendered = true;
    this.signalComplete();
  }

  renderNarrationPill() {
    if (!this.hasNarrationPillTarget) return;
    const reviewer = this.reviewerFirstNameValue;
    if (!reviewer) {
      // No reviewer to attribute to (place-primary
      // photo or no-candidates fallback). Leave the
      // slot empty: the bare "Tu mejor foto"
      // headline is the right call there.
      this.narrationPillTarget.replaceChildren();
      return;
    }
    const pill = document.createElement("span");
    pill.className =
      "inline-block px-2.5 py-0.5 rounded-md " +
      "bg-[#FDE047]";
    pill.dataset.testid = "winner-pill-name";
    pill.textContent = `la tomó ${reviewer}.`;
    this.narrationPillTarget.replaceChildren(pill);
  }

  renderWinnerCard() {
    if (!this.hasWinnerSlotTarget) return;
    const html = this.winnerCardHtmlValue;
    if (!html) return;
    this.winnerSlotTarget.innerHTML = html;
  }

  renderScoreCard() {
    if (!this.hasScoreCardSlotTarget) return;
    const chunks = this.scoreRowHtmlValue;
    if (!Array.isArray(chunks) || !chunks.length) {
      return;
    }
    this.scoreCardSlotTarget.innerHTML =
      chunks.join("");
  }

  renderDiscardedGrid() {
    if (!this.hasDiscardedSlotTarget) return;
    const chunks = this.discardedTileHtmlValue;
    if (!Array.isArray(chunks)) return;
    // Empty array is a legitimate state (no
    // discarded candidates). Replace whatever was
    // there (skeleton tiles) with nothing: leaves
    // the grid empty rather than stuck in skeleton.
    this.discardedSlotTarget.innerHTML =
      chunks.join("");
  }

  // -------- completion --------

  signalComplete() {
    // Stamp the parent stage element with a
    // completion flag BEFORE dispatching the event.
    // Stimulus controller connect order is non-
    // deterministic: if this controller connects
    // and fires the event before audit-continue-
    // button attaches its listener, the event is
    // lost. The flag lets the button's connect() see
    // the completion regardless of attach order.
    const stage = this.element.closest(
      '[data-audit-stage-target="stage"]'
    );
    if (stage) {
      stage.dataset.auditStepComplete = "true";
    }

    document.dispatchEvent(
      new CustomEvent("audit-step:complete", {
        detail: {stepKey: "picking_photo"},
      })
    );
  }
}
